import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import axios from 'axios';
import { COMPETITONS_API } from 'src/apiConfig';
import { useTranslation } from 'react-i18next';
import { modeDescriptions } from 'src/constants/competitions';
import { IOSSwitch } from './helper';

const AdminModovi = () => {
  const { t } = useTranslation();

  const [modovi, setModovi] = useState([]);
  const [modifiedModovi, setModifiedModovi] = useState([]);
  const [loading, setLoading] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // could be 'success' or 'error'

  // Dialog for cleaning
  const [openCleanDialog, setOpenCleanDialog] = useState(false);

  useEffect(() => {
    fetchModovi();
  }, []);

  const fetchModovi = async () => {
    try {
      const response = await axios.get(`${COMPETITONS_API}`);
      setModovi(response.data);
      setModifiedModovi(response.data);
    } catch (error) {
      console.error(t('adminModovi.fetchError'), error);
    }
  };

  const handleToggle = (modId) => {
    setModifiedModovi((prevModovi) =>
      prevModovi.map((mod) => {
        if (mod.id === modId) {
          return { ...mod, aktivan: mod.aktivan === '1' ? '0' : '1' };
        }
        return { ...mod, aktivan: '0' };
      })
    );
  };

  const handleSaveChanges = async () => {
    setLoading(true);
    try {
      const changedModovi = modifiedModovi.filter(
        (modifiedMod, index) => modovi[index].aktivan !== modifiedMod.aktivan
      );

      await Promise.all(
        changedModovi.map(async (mod) => {
          const response = await axios.put(`${COMPETITONS_API}/${mod.id}`, {
            aktivan: mod.aktivan
          });
          if (response.status !== 200) {
            throw new Error(t('adminModovi.updateError') + ` (id: ${mod.id})`);
          }
        })
      );

      setSnackbarMessage(t('adminModovi.saveSuccess'));
      setSnackbarSeverity('success');
      fetchModovi();
    } catch (error) {
      console.error(t('adminModovi.saveError'), error);
      setSnackbarMessage(t('adminModovi.saveError'));
      setSnackbarSeverity('error');
    } finally {
      setLoading(false);
      setSnackbarOpen(true);
    }
  };

  // 1. Open clean/archiving dialog
  const handleOpenCleanDialog = () => {
    setOpenCleanDialog(true);
  };

  // 2. Close clean/archiving dialog
  const handleCloseCleanDialog = () => {
    setOpenCleanDialog(false);
  };

  // 3. Confirm the cleanup/archiving
  const handleConfirmClean = async () => {
    try {
      // Make the GET request with the `all_delete` param
      const response = await axios.get(`${COMPETITONS_API}?all_delete=1`);
      if (response.data?.success) {
        setSnackbarMessage(
          response.data.message || t('adminModovi.cleanSuccess')
        );
        setSnackbarSeverity('success');
      } else if (response.data?.error) {
        setSnackbarMessage(response.data.error);
        setSnackbarSeverity('error');
      } else {
        setSnackbarMessage(t('adminModovi.cleanSuccess'));
        setSnackbarSeverity('success');
      }
    } catch (error) {
      console.error(t('adminModovi.cleanError'), error);
      setSnackbarMessage(t('adminModovi.cleanError'));
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
      setOpenCleanDialog(false);
    }
  };

  return (
    <Paper elevation={4} sx={{ padding: '30px', backgroundColor: '#f0f4f7' }}>
      <Box m="20px">
        {/* 
          1) Title + subtitle 
          2) The Clean button at the top 
        */}
        <Typography
          variant="h4"
          mb={3}
          textAlign="center"
          sx={{ fontWeight: 'bold', color: '#1c75bc' }}
        >
          {t('adminModovi.title')}
        </Typography>
        <Typography
          variant="subtitle1"
          mb={5}
          textAlign="center"
          sx={{ color: '#2596be' }}
        >
          {t('adminModovi.subtitle')}
        </Typography>

        {/* CLEAN / ARCHIVE BUTTON */}
        <Box display="flex" justifyContent="center" mb={3}>
          <Button
            variant="contained"
            color="error"
            onClick={handleOpenCleanDialog}
            sx={{
              fontWeight: 'bold',
              borderRadius: '12px',
              fontSize: '16px',
              textTransform: 'none'
            }}
          >
            {t('adminModovi.cleanArchiveButton')}
            {/* e.g. "Archive & Clean" */}
          </Button>
        </Box>

        {/* List of mods */}
        {modifiedModovi.map((mod) => (
          <Paper
            key={mod.id}
            elevation={3}
            sx={{
              padding: '20px',
              marginBottom: '15px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: mod.aktivan === '1' ? '#bbdefb' : '#f5f5f5',
              borderRadius: '12px',
              borderLeft:
                mod.aktivan === '1' ? '5px solid #1976d2' : '5px solid #e0e0e0'
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 'bold', color: '#1c75bc' }}
              >
                {t(`modeDescriptions.${mod.naziv}`, {
                  defaultValue: modeDescriptions[mod.naziv] || mod.naziv
                })}
              </Typography>
              <Typography variant="body2" sx={{ color: '#333' }}>
                {mod.opis || t('adminModovi.noDescription')}
              </Typography>
            </Box>
            <IOSSwitch
              checked={mod.aktivan === '1'}
              onChange={() => handleToggle(mod.id)}
              color="primary"
            />
          </Paper>
        ))}

        {/* SAVE CHANGES BUTTON */}
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSaveChanges}
          disabled={loading}
          sx={{
            mt: 3,
            display: 'block',
            mx: 'auto',
            backgroundColor: '#ff4081',
            color: '#fff',
            padding: '12px 24px',
            fontWeight: 'bold',
            borderRadius: '12px',
            fontSize: '16px',
            transition: 'background-color 0.3s ease',
            '&:hover': {
              backgroundColor: '#f50057'
            }
          }}
        >
          {t('adminModovi.saveChanges')}
        </Button>

        {/* SNACKBAR for success/error */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ borderRadius: '8px' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>

      {/* DIALOG for Clean/Archive Confirmation */}
      <Dialog
        open={openCleanDialog}
        onClose={handleCloseCleanDialog}
        aria-labelledby="clean-dialog-title"
        aria-describedby="clean-dialog-description"
      >
        <DialogTitle id="clean-dialog-title">
          {t('adminModovi.cleanDialogTitle')}
          {/* e.g. "Are you sure you want to clean everything?" */}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="clean-dialog-description">
            {t('adminModovi.cleanDialogContent')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCleanDialog}>
            {t('adminModovi.cancel')}
          </Button>
          <Button onClick={handleConfirmClean} color="error" autoFocus>
            {t('adminModovi.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdminModovi;
